import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Shield, Save, Plus, X, AlertTriangle, Clock, Smartphone } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface Organization {
  id: string;
  name: string;
}

interface SecurityTabProps {
  organization: Organization;
  onSave: (data: any) => void;
}

export function SecurityTab({ organization, onSave }: SecurityTabProps) {
  const { toast } = useToast()
  
  const [ipRestrictions, setIpRestrictions] = useState({
    enabled: false,
    allow_list: ['192.168.1.0/24', '10.0.0.0/8'],
    new_ip: ''
  })

  const [sessionSettings, setSessionSettings] = useState({
    timeout: 480, // minutes
    require_activity: true,
    remember_me: true
  })

  const [twoFactor, setTwoFactor] = useState({
    enabled: false,
    enforce_for_admins: true,
    backup_codes: 8
  })

  const handleSave = () => {
    onSave({ ipRestrictions, sessionSettings, twoFactor })
  }

  const addIpRange = () => {
    if (ipRestrictions.new_ip && isValidCIDR(ipRestrictions.new_ip)) {
      setIpRestrictions(prev => ({
        ...prev,
        allow_list: [...prev.allow_list, prev.new_ip],
        new_ip: ''
      }))
    } else {
      toast({
        title: "Ogiltigt IP-intervall",
        description: "Ange ett giltigt CIDR-format, t.ex. 192.168.1.0/24",
        variant: "destructive"
      })
    }
  }

  const removeIpRange = (index: number) => {
    setIpRestrictions(prev => ({
      ...prev,
      allow_list: prev.allow_list.filter((_, i) => i !== index)
    }))
  }

  const isValidCIDR = (cidr: string) => {
    // Basic CIDR validation
    const cidrRegex = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$/
    return cidrRegex.test(cidr)
  }

  const enable2FA = () => {
    toast({
      title: "2FA aktiverad",
      description: "Tvåfaktorsautentisering har aktiverats för organisationen.",
    })
    setTwoFactor(prev => ({ ...prev, enabled: true }))
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            IP-begränsningar
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Begränsa åtkomst till specifika IP-adresser eller nätverk
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Aktivera IP-begränsningar</Label>
              <p className="text-sm text-muted-foreground">Endast tillåtna IP-adresser kan komma åt systemet</p>
            </div>
            <Switch
              checked={ipRestrictions.enabled}
              onCheckedChange={(checked) => setIpRestrictions(prev => ({ ...prev, enabled: checked }))}
            />
          </div>

          {ipRestrictions.enabled && (
            <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 p-3 bg-orange-50 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <span className="text-sm text-orange-800">
                  Var försiktig! Felaktig konfiguration kan låsa ut alla användare.
                </span>
              </div>

              <div>
                <Label className="text-base font-medium">Tillåtna IP-intervall (CIDR)</Label>
                <div className="space-y-2 mt-2">
                  {ipRestrictions.allow_list.map((ip, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                      <span className="font-mono text-sm">{ip}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeIpRange(index)}
                        className="text-red-600 hover:text-red-800 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2 mt-3">
                  <Input
                    value={ipRestrictions.new_ip}
                    onChange={(e) => setIpRestrictions(prev => ({ ...prev, new_ip: e.target.value }))}
                    placeholder="192.168.1.0/24"
                    className="font-mono"
                  />
                  <Button onClick={addIpRange} variant="outline" className="gap-2">
                    <Plus className="h-4 w-4" />
                    Lägg till
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Använd CIDR-notation, t.ex. 192.168.1.0/24 för hela subnät eller 203.0.113.1/32 för en specifik IP.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Sessionsinställningar
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="session_timeout">Sessionstid (minuter)</Label>
            <Input
              id="session_timeout"
              type="number"
              value={sessionSettings.timeout}
              onChange={(e) => setSessionSettings(prev => ({ ...prev, timeout: parseInt(e.target.value) }))}
              min="30"
              max="1440"
              step="30"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Tid innan inaktiva användare loggas ut automatiskt
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Kräv aktivitet</Label>
              <p className="text-sm text-muted-foreground">Logga ut vid inaktivitet även om sidan är öppen</p>
            </div>
            <Switch
              checked={sessionSettings.require_activity}
              onCheckedChange={(checked) => setSessionSettings(prev => ({ ...prev, require_activity: checked }))}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">"Kom ihåg mig"</Label>
              <p className="text-sm text-muted-foreground">Tillåt användare att förbli inloggade längre</p>
            </div>
            <Switch
              checked={sessionSettings.remember_me}
              onCheckedChange={(checked) => setSessionSettings(prev => ({ ...prev, remember_me: checked }))}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Tvåfaktorsautentisering (2FA)
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Förstärk säkerheten med tvåfaktorsautentisering
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">2FA för organisationen</Label>
              <p className="text-sm text-muted-foreground">Kräv 2FA för alla användare i organisationen</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={twoFactor.enabled ? "default" : "secondary"}>
                {twoFactor.enabled ? "Aktiverad" : "Inaktiv"}
              </Badge>
              <Switch
                checked={twoFactor.enabled}
                onCheckedChange={(checked) => setTwoFactor(prev => ({ ...prev, enabled: checked }))}
              />
            </div>
          </div>

          {!twoFactor.enabled && (
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Aktivera 2FA</h4>
              <p className="text-sm text-blue-800 mb-3">
                Tvåfaktorsautentisering ger ett extra lager av säkerhet genom att kräva både lösenord och en kod från användarens mobila enhet.
              </p>
              <Button onClick={enable2FA} className="gap-2">
                <Smartphone className="h-4 w-4" />
                Aktivera 2FA nu
              </Button>
            </div>
          )}

          {twoFactor.enabled && (
            <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Tvinga 2FA för administratörer</Label>
                  <p className="text-sm text-muted-foreground">Kräv 2FA för alla användare med admin-behörighet</p>
                </div>
                <Switch
                  checked={twoFactor.enforce_for_admins}
                  onCheckedChange={(checked) => setTwoFactor(prev => ({ ...prev, enforce_for_admins: checked }))}
                />
              </div>

              <div>
                <Label>Reservkoder per användare</Label>
                <Input
                  type="number"
                  value={twoFactor.backup_codes}
                  onChange={(e) => setTwoFactor(prev => ({ ...prev, backup_codes: parseInt(e.target.value) }))}
                  min="4"
                  max="16"
                  className="w-32"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Antal engångskoder som genereras för nödåtkomst
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2">
          <Save className="h-4 w-4" />
          Spara säkerhetsinställningar
        </Button>
      </div>
    </div>
  )
}